import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react';

export type SearchableSelectOption = {
  value: string | number;
  label: string;
  sublabel?: string;
};

interface SearchableSelectProps {
  id: string;
  label?: string;
  /** Selected value (entity id), or null when nothing is selected. */
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  /** Server-side typeahead. `page` (1-indexed) enables incremental "load more". */
  loadOptions: (search: string, page: number) => Promise<{ options: SearchableSelectOption[]; hasMore: boolean }>;
  /** Resolve the label for a preselected value not yet in the list (edit-mode hydration). */
  loadSelected?: (value: string | number) => Promise<SearchableSelectOption | null>;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  /** Show a clear (✕) button — e.g. for an "All" filter. */
  clearable?: boolean;
  required?: boolean;
  className?: string;
}

const DEBOUNCE_MS = 300;

/**
 * SearchableSelect
 *
 * A reusable, mobile-first, single inline searchable dropdown that fetches its
 * options from the server as the user types (server-side typeahead). Built on
 * @headlessui/react Combobox for keyboard/a11y support. Controlled via
 * `value`/`onChange` so it works with both react-hook-form `Controller` and
 * plain `useState` filters.
 */
function SearchableSelect({
  id,
  label,
  value,
  onChange,
  loadOptions,
  loadSelected,
  placeholder = 'Search…',
  error,
  disabled = false,
  clearable = false,
  required = false,
  className = '',
}: SearchableSelectProps) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SearchableSelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SearchableSelectOption | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Keep latest callbacks in refs so non-memoized inline functions from the
  // parent don't retrigger the search/hydration effects on every render.
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;
  const loadSelectedRef = useRef(loadSelected);
  loadSelectedRef.current = loadSelected;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const runSearch = useCallback((q: string, p: number) => {
    setLoading(true);
    loadOptionsRef.current(q, p)
      .then(({ options: opts, hasMore: more }) => {
        setOptions((prev) => (p === 1 ? opts : [...prev, ...opts]));
        setHasMore(more);
        setPage(p);
      })
      .catch(() => {
        if (p === 1) {
          setOptions([]);
          setHasMore(false);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Debounced search whenever the query changes (also loads the first page on mount).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query, 1), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  // Hydrate the selected option's label when `value` is set externally (edit mode).
  useEffect(() => {
    let cancelled = false;
    if (value === null || value === undefined || value === '' || value === 0) {
      setSelectedOption(null);
      return;
    }
    if (selectedOption && selectedOption.value === value) return;
    const found = options.find((o) => o.value === value);
    if (found) {
      setSelectedOption(found);
      return;
    }
    if (loadSelectedRef.current) {
      loadSelectedRef.current(value).then((opt) => {
        if (!cancelled && opt) setSelectedOption(opt);
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const handleChange = (opt: SearchableSelectOption | null) => {
    setSelectedOption(opt);
    onChange(opt ? opt.value : null);
  };

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      runSearch(query, page + 1);
    }
  };

  const inputClasses =
    'w-full rounded-md border bg-white px-3 py-2.5 pr-16 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 sm:py-2 sm:text-sm ' +
    (error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500') +
    (disabled ? ' bg-gray-100 cursor-not-allowed' : '');

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <Combobox
        value={selectedOption}
        by="value"
        onChange={handleChange}
        disabled={disabled}
        immediate
        onClose={() => setQuery('')}
      >
        <div className="relative">
          <ComboboxInput
            id={id}
            autoComplete="off"
            className={inputClasses}
            displayValue={(opt: SearchableSelectOption | null) => opt?.label ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
          />

          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={() => {
                handleChange(null);
                setQuery('');
              }}
              className="absolute inset-y-0 right-7 flex items-center px-1 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 8.586l4.293-4.293 1.414 1.414L11.414 10l4.293 4.293-1.414 1.414L10 11.414l-4.293 4.293-1.414-1.414L8.586 10 4.293 5.707l1.414-1.414L10 8.586z" clipRule="evenodd" />
              </svg>
            </button>
          )}

          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </ComboboxButton>

          <ComboboxOptions
            onScroll={handleScroll}
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
          >
            {loading && options.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">Loading…</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">{query ? 'No matches found' : 'No results'}</div>
            ) : (
              options.map((opt) => (
                <ComboboxOption
                  key={opt.value}
                  value={opt}
                  className={({ active }) =>
                    `cursor-pointer select-none px-3 py-2.5 sm:py-2 ${active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'}`
                  }
                >
                  {({ selected }) => (
                    <div className="flex flex-col">
                      <span className={selected ? 'font-medium' : 'font-normal'}>{opt.label}</span>
                      {opt.sublabel && <span className="text-xs text-gray-500">{opt.sublabel}</span>}
                    </div>
                  )}
                </ComboboxOption>
              ))
            )}
            {loading && options.length > 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">Loading more…</div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default SearchableSelect;
